import { useEffect, useState } from 'react'

// Controls hang just below an embed's bottom edge (the resize bar), so count that strip as inside.
const BOTTOM_SLACK = 6
// How long after a scroll stops before re-checking where the pointer is.
const SCROLL_SETTLE_MS = 150

const contains = (box: DOMRect, x: number, y: number): boolean =>
  x >= box.left && x <= box.right && y >= box.top && y <= box.bottom + BOTTOM_SLACK

/**
 * Whether the pointer is over `element`. In edit mode the editor turns pointer events off for embedded
 * tables and dashboards (style.scss), so they never hear the pointer arrive — this follows it at
 * document level and checks it against the element's box instead.
 *
 * Only pointer moves over the element's own layer count — the element itself, or the editor content
 * the pointer reaches through it — so a menu or dropdown laid over the embed (an open Edit dialog)
 * doesn't count as hovering the embed beneath it.
 *
 * A scroll that moves the element reports false until scrolling settles, then re-checks the last
 * pointer position: anything anchored when it shows (a tooltip) is placed afresh rather than left
 * behind. Scrolls that don't move the element, like the embedded grid's own, are ignored.
 */
const usePointerWithin = (element: HTMLElement | null, enabled: boolean): boolean => {
  const [within, setWithin] = useState(false)

  useEffect(() => {
    if (!element || !enabled) {
      setWithin(false)
      return
    }
    // Pointer events fall through the embed to the editor around it, so that's the layer it lives on.
    const layer = element.closest('.DraftEditor-root') ?? element
    let pointer: { x: number; y: number; onLayer: boolean } | undefined
    let lastBox = element.getBoundingClientRect()
    let settleTimer: number | undefined

    const onMove = (event: MouseEvent) => {
      const onLayer = event.target instanceof Node && layer.contains(event.target)
      pointer = { x: event.clientX, y: event.clientY, onLayer }
      lastBox = element.getBoundingClientRect()
      setWithin(onLayer && contains(lastBox, pointer.x, pointer.y))
    }
    const onScroll = () => {
      const box = element.getBoundingClientRect()
      if (box.top === lastBox.top && box.left === lastBox.left) return
      lastBox = box
      setWithin(false)
      window.clearTimeout(settleTimer)
      settleTimer = window.setTimeout(() => {
        if (pointer) setWithin(pointer.onLayer && contains(element.getBoundingClientRect(), pointer.x, pointer.y))
      }, SCROLL_SETTLE_MS)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => {
      window.clearTimeout(settleTimer)
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('scroll', onScroll, { capture: true })
    }
  }, [element, enabled])

  return within
}

export default usePointerWithin
